import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldCheck, ArrowRight, CalendarDays, Clock } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { useSEO } from "../hooks/useSEO";
import { site, quoteTypes } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = [
  "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30",
];

function buildDays() {
  const days = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (days.length < 14) {
    if (d.getDay() !== 0) {
      days.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return days;
}

const dayLabel = (date) =>
  date.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-[13px] font-semibold text-forest mb-1.5">
    {children}
  </label>
);

export default function RendezVous() {
  useSEO({
    title: "Prendre rendez-vous en ligne — Jardinage Charleroi, Namur, Mons | Embek Group",
    description:
      "Choisissez votre prestation, votre date et votre créneau : prenez rendez-vous en ligne avec Embek Group pour l'entretien de votre jardin à Charleroi, Namur et Mons.",
  });

  const days = buildDays();
  const [form, setForm] = useState({ prestation: "", nom: "", telephone: "", email: "", adresse: "", message: "", privacy_acceptee: false });
  const [date, setDate] = useState(days[0]);
  const [time, setTime] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const dateStr = (d) => d.toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    if (!time) {
      toast.error("Choisissez un créneau horaire.");
      return;
    }
    if (!form.privacy_acceptee) {
      toast.error("Veuillez accepter la politique de confidentialité.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/appointments`, { ...form, date: dateStr(date), heure: time });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <>
        <PageHeader eyebrow="Rendez-vous" title="Demande envoyée !" />
        <section className="py-16 lg:py-24">
          <div className="max-w-2xl mx-auto px-4 sm:px-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid="rdv-success"
              className="bg-white rounded-3xl border border-forest/10 shadow-xl shadow-forest/5 p-10 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 14 }}
                className="mx-auto w-16 h-16 rounded-full bg-forest/10 flex items-center justify-center"
              >
                <CheckCircle2 size={34} className="text-moss" />
              </motion.div>
              <h2 className="mt-6 font-serif text-3xl text-forest">Merci {form.nom} !</h2>
              <p className="mt-3 text-coal-muted leading-relaxed">
                Votre demande de rendez-vous pour <strong className="text-forest">{form.prestation}</strong> a bien
                été enregistrée.
              </p>
              <div className="mt-6 inline-flex flex-col sm:flex-row gap-3 sm:gap-6 bg-sand-mid rounded-2xl px-6 py-4">
                <span className="flex items-center justify-center gap-2 text-sm font-semibold text-forest">
                  <CalendarDays size={16} className="text-moss" /> {dayLabel(date)}
                </span>
                <span className="flex items-center justify-center gap-2 text-sm font-semibold text-forest">
                  <Clock size={16} className="text-moss" /> {time}
                </span>
              </div>
              <p className="mt-5 text-sm text-coal-muted">
                Nous vous confirmons le rendez-vous par téléphone ou par e-mail dans les plus brefs délais.
              </p>
              <button
                onClick={() => { setDone(false); setForm({ ...form, message: "" }); setTime(""); }}
                className="btn-primary mt-8"
                data-testid="rdv-reset-button"
              >
                Prendre un autre rendez-vous
              </button>
            </motion.div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Rendez-vous en ligne"
        title="Prenez rendez-vous"
        description="Choisissez votre prestation, votre date et votre créneau : nous confirmons votre rendez-vous par téléphone ou par e-mail."
      />

      <section className="py-16 lg:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal>
            <form onSubmit={submit} data-testid="rdv-form" className="bg-white rounded-3xl border border-forest/10 shadow-xl shadow-forest/5 p-6 sm:p-9 space-y-7">
              {/* 1. Prestation */}
              <div>
                <Label htmlFor="r-prestation">1. Votre prestation *</Label>
                <select id="r-prestation" required data-testid="rdv-input-prestation" className="field" value={form.prestation} onChange={set("prestation")}>
                  <option value="" disabled>Choisir une prestation…</option>
                  {quoteTypes.filter((t) => t !== "Autre demande").map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* 2. Date */}
              <div>
                <Label htmlFor="r-date">2. Choisissez un jour *</Label>
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="rdv-day-picker">
                  {days.map((d) => {
                    const active = dateStr(d) === dateStr(date);
                    return (
                      <button
                        type="button"
                        key={dateStr(d)}
                        onClick={() => setDate(d)}
                        data-testid={`rdv-day-${dateStr(d)}`}
                        className={`shrink-0 px-4 py-2.5 rounded-full text-[13px] font-semibold capitalize transition-all duration-300 ${
                          active
                            ? "bg-forest text-sand shadow-lg shadow-forest/20"
                            : "bg-sand-mid text-forest/70 hover:bg-sand-dark/60"
                        }`}
                      >
                        {dayLabel(d)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 3. Heure */}
              <div>
                <Label>3. Choisissez un créneau *</Label>
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="rdv-slot-picker">
                  {SLOTS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setTime(s)}
                      data-testid={`rdv-slot-${s}`}
                      className={`px-2 py-2.5 rounded-xl text-[13px] font-semibold transition-all duration-300 ${
                        time === s
                          ? "bg-moss text-white shadow-lg shadow-moss/25"
                          : "bg-sand-mid text-forest/70 hover:bg-sand-dark/60"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-coal/45">Créneaux indicatifs — déjeuner 12h00–13h30. Dimanches exclus.</p>
              </div>

              {/* 4. Coordonnées */}
              <div>
                <Label>4. Vos coordonnées *</Label>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required data-testid="rdv-input-nom" className="field" value={form.nom} onChange={set("nom")} placeholder="Nom et prénom *" />
                  <input required type="tel" data-testid="rdv-input-telephone" className="field" value={form.telephone} onChange={set("telephone")} placeholder="Téléphone *" />
                  <input required type="email" data-testid="rdv-input-email" className="field" value={form.email} onChange={set("email")} placeholder="E-mail *" />
                  <input required data-testid="rdv-input-adresse" className="field" value={form.adresse} onChange={set("adresse")} placeholder="Adresse / commune *" />
                </div>
                <textarea rows={3} data-testid="rdv-input-message" className="field resize-none mt-4" value={form.message} onChange={set("message")} placeholder="Précisions utiles (accès, matériel, détails du chantier…) — optionnel" />
              </div>

              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="rdv-privacy-wrapper">
                <input type="checkbox" checked={form.privacy_acceptee} onChange={set("privacy_acceptee")} className="mt-0.5 w-4 h-4 accent-[#1E3A2B]" data-testid="rdv-input-privacy" />
                <span className="text-[13px] leading-relaxed text-coal-muted">
                  J'accepte que mes informations soient utilisées pour organiser ce rendez-vous, conformément à la{" "}
                  <a href="/politique-de-confidentialite" className="text-moss font-semibold underline underline-offset-2">politique de confidentialité</a>. *
                </span>
              </label>

              <button type="submit" disabled={sending} data-testid="rdv-submit" className="btn-primary w-full text-base disabled:opacity-60 disabled:pointer-events-none">
                {sending ? (
                  <>
                    <Loader2 size={17} className="animate-spin" /> Envoi en cours…
                  </>
                ) : (
                  <>
                    Confirmer ma demande de rendez-vous <ArrowRight size={16} />
                  </>
                )}
              </button>
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-coal/45">
                <ShieldCheck size={13} className="text-moss" /> Confirmation par téléphone ou e-mail — sans engagement
              </p>
            </form>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="mt-6 text-center text-sm text-coal-muted">
              Vous ne savez pas encore de quel créneau vous avez besoin ?{" "}
              <a href="/demander-un-devis" data-testid="rdv-to-devis-link" className="text-forest font-semibold underline underline-offset-4 decoration-moss/60">
                Demandez d'abord un devis gratuit
              </a>.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
